import { faCheck, faX } from "@fortawesome/pro-regular-svg-icons";
import { Grid, Stack, Typography, useTheme } from "@mui/material";

import { Modality } from "@/components/graphql/schema.graphql";
import { StyleCapabilities } from "@/types";

import { Icon } from "../../../Icon/Icon";
import { getCapabilityConfig } from "./utils";

interface CapabilityItemProps {
  label: string;
  isSupported: boolean;
  size?: number;
}

const CapabilityItem = (props: CapabilityItemProps) => {
  const { label, isSupported, size = 6 } = props;

  const theme = useTheme();

  return (
    <Grid size={size}>
      <Stack
        direction="row"
        gap={2}
        sx={{
          alignItems: "center",
          color: isSupported ? theme.palette.success.main : undefined,
        }}
      >
        <Icon
          icon={isSupported ? faCheck : faX}
          style={{ width: 14, textAlign: "center" }}
        />
        <Typography variant="caption">{label}</Typography>
      </Stack>
    </Grid>
  );
};

interface ModelCapabilitiesProps {
  capabilities?: StyleCapabilities;
  modality?: Modality;
}

export const ModelCapabilities = (props: ModelCapabilitiesProps) => {
  const { modality, capabilities } = props;

  if (!capabilities) {
    return null;
  }

  const capabilityConfig = getCapabilityConfig(modality || "IMAGE");
  const generalCapabilities = capabilityConfig.filter(
    (c) => c.section === "general",
  );
  const referenceImageCapabilities = capabilityConfig.filter(
    (c) => c.section === "referenceImageTypes",
  );
  const configurationCapabilities = capabilityConfig.filter(
    (c) => c.section === "configuration",
  );

  const sectionlessCapabilities = capabilityConfig.filter((c) => !c.section);

  return (
    <Stack direction="column" spacing={3}>
      {generalCapabilities.length > 0 && (
        <Stack direction="column" spacing={1}>
          <Typography>General</Typography>
          <Grid container columnSpacing={4}>
            {generalCapabilities.map((config) => (
              <CapabilityItem
                key={config.key}
                label={config.label}
                isSupported={config.getIsSupported(capabilities)}
                size={12}
              />
            ))}
          </Grid>
        </Stack>
      )}

      {referenceImageCapabilities.length > 0 && (
        <Stack direction="column" spacing={1}>
          <Typography>Reference Image Types</Typography>
          <Grid container columnSpacing={4}>
            {referenceImageCapabilities.map((config) => (
              <CapabilityItem
                key={config.key}
                label={config.label}
                isSupported={config.getIsSupported(capabilities)}
              />
            ))}
          </Grid>
        </Stack>
      )}

      {configurationCapabilities.length > 0 && (
        <Stack direction="column" spacing={1}>
          <Typography>Configuration</Typography>
          <Grid container columnSpacing={4}>
            {configurationCapabilities.map((config) => (
              <CapabilityItem
                key={config.key}
                label={config.label}
                isSupported={config.getIsSupported(capabilities)}
              />
            ))}
          </Grid>
        </Stack>
      )}

      {sectionlessCapabilities.length > 0 && (
        <Stack direction="column" spacing={1}>
          <Typography>Capabilities</Typography>
          <Grid container columnSpacing={4}>
            {sectionlessCapabilities.map((config) => (
              <CapabilityItem
                key={config.key}
                label={config.label}
                isSupported={config.getIsSupported(capabilities)}
              />
            ))}
          </Grid>
        </Stack>
      )}
    </Stack>
  );
};
